// Demo page: NextAdmin /auth/reset-password (new password form) and /mail-success (confirmation).
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { z } from 'zod';
import { resetPasswordMutation } from '../generated/api/@tanstack/react-query.gen.js';
import { zResetPasswordRequest } from '../generated/api/zod.gen.js';
import { Form, SubmitButton, TextFormField, useZodForm } from '../components/form/index.js';
import { AuthLayout } from '../components/page/AuthLayout.js';
import { buttonStyles } from '../components/tailgrids/core/button.js';

/** The generated reset body (minus the link's token), plus the UI-only confirmation field. */
const resetSchema = zResetPasswordRequest
  .pick({ password: true })
  .extend({ confirmPassword: z.string() })
  .refine((values) => values.password === values.confirmPassword, {
    path: ['confirmPassword'],
    message: 'The passwords do not match',
  });

const signInLink = (
  <Link to="/login" className="font-medium text-accent-text hover:underline">
    Sign in
  </Link>
);

export function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const resetToken = searchParams.get('token') ?? '';
  const [done, setDone] = useState(false);
  const reset = useMutation(resetPasswordMutation());
  const form = useZodForm(resetSchema, { defaultValues: { password: '', confirmPassword: '' } });

  const submit = async ({ password }: z.output<typeof resetSchema>) => {
    await reset.mutateAsync({ body: { token: resetToken, password } });
    setDone(true);
  };

  if (!resetToken) {
    return (
      <AuthLayout
        title="This reset link is not valid"
        subtitle="The link is missing its token. Open the link from your email again, or ask an administrator for a new one."
      >
        <Link to="/login" className={buttonStyles({ size: 'lg', className: 'w-full' })}>
          Back to sign in
        </Link>
      </AuthLayout>
    );
  }

  if (done) {
    return (
      <AuthLayout title="Password updated" subtitle="You can now sign in with your new password.">
        <Link to="/login" className={buttonStyles({ size: 'lg', className: 'w-full' })}>
          Go to sign in
        </Link>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Set a new password"
      subtitle="Choose a password for your Conflui account"
      footer={<>Remembered it? {signInLink}</>}
    >
      <Form form={form} onSubmit={submit}>
        <TextFormField
          name="password"
          type="password"
          label="New password"
          description="At least 8 characters."
          autoComplete="new-password"
        />
        <TextFormField name="confirmPassword" type="password" label="Confirm password" autoComplete="new-password" />
        <SubmitButton size="lg" className="w-full">
          Set password
        </SubmitButton>
      </Form>
    </AuthLayout>
  );
}
